import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useRecordContext } from 'react-admin';

import sanitizeRestProps from './sanitizeRestProps';
import { PublicFieldProps, InjectedFieldProps, fieldPropTypes } from './types';

import { FileIcon } from '../../../themes/icons/FileIcon';
import { PdfIcon } from '../../../themes/icons/PdfIcon';
import { WordIcon } from '../../../themes/icons/WordIcon';
import { ExcelIcon } from '../../../themes/icons/ExcelIcon';
import { CompressedIcon } from '../../../themes/icons/CompressedIcon';

const useStyles = makeStyles({
  root: { display: 'inline-block' },
  sizeLabel: { color: '#A4A4A4' },
  fileTitle: { color: '#2962FF' }
});

const fileIconStyle = {
  width: '24px',
  height: '24px',
  paddingRight: '8px'
};

const getFileIcon = (fileType, classes) => {
  switch (fileType) {
    case 'application/pdf':
      return <PdfIcon style={fileIconStyle} />;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/msword':
      return <WordIcon style={fileIconStyle} />;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
      return <ExcelIcon style={fileIconStyle} />;
    case 'application/zip':
    case 'application/octet-stream':
    case 'application/x-zip-compressed':
    case 'multipart/x-zip':
      return <CompressedIcon style={fileIconStyle} />;
    default:
      return <FileIcon style={fileIconStyle} />;
  }
};

interface Props extends PublicFieldProps {
  src?: string;
  title?: string;
  target?: string;
  download?: boolean;
  classes?: object;
  size?: number;
  type?: string;
}

export const FileField = (props: Props & InjectedFieldProps) => {
  const {
    className,
    classes: classesOverride,
    size,
    source,
    title,
    src,
    target,
    type,
    download,
    ...rest
  } = props;
  const record = useRecordContext(props);
  const sourceValue = get(record, source || '');
  const classes = useStyles({ classes: classesOverride });

  if (!sourceValue) {
    return (
      <div
        className={classnames(classes.root, className)}
        {...sanitizeRestProps(rest)}
      />
    );
  }

  if (Array.isArray(sourceValue)) {
    return (
      <ul
        className={classnames(classes.root, className)}
        {...sanitizeRestProps(rest)}
      >
        {sourceValue.map((file, index) => {
          const fileTitleValue = get(file, title);
          const srcValue = get(file, src);
          const sizeLabel = get(file, size);
          const fileType = get(file, type);

          return (
            <li key={index}>
              {getFileIcon(fileType, classes)}
              <a
                href={srcValue}
                title={fileTitleValue}
                target={target}
                className={classes.fileTitle}
              >
                {fileTitleValue}
              </a>
              <span className={classes.sizeLabel}>{sizeLabel}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  const titleValue = get(record, title);
  const sizeLabel = get(record, size);
  const fileType = get(record, type);

  return (
    <div
      className={classnames(classes.root, className)}
      {...sanitizeRestProps(rest)}
    >
      {getFileIcon(fileType, classes)}
      <a
        href={sourceValue}
        title={titleValue}
        target={target}
        className={classes.fileTitle}
        download={download && titleValue}
      >
        {titleValue}
      </a>
      <span className={classes.sizeLabel}>{sizeLabel}</span>
    </div>
  );
};

FileField.defaultProps = {
  addLabel: true
};

FileField.propTypes = {
  ...fieldPropTypes,
  src: PropTypes.string,
  title: PropTypes.string,
  target: PropTypes.string
};

export default FileField;
